import React, { useEffect, useRef, useState } from 'react';
import { useDebounce, useWindowSize } from '@uidotdev/usehooks';
import clsx from 'clsx';

import { Menu, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import '@fortawesome/fontawesome-svg-core/styles.css';

import './header.css';

import { useHeaderState } from '../../../../global/lib/public/headerState';
import { routes } from '../../../../global/lib/public/routes';

// const logoText = 'Center for the Study of Services Logo (CSS)';
// const baseURL = 'https://public.cssresearch.org';
// const logoWidth = 109;
// const logoHeight = 67;

interface HeaderProps {
    /** White version of the logo */
    inverseLogo: boolean;
    /** Dark version of the logo */
    darkMode: boolean; 
};

export default function Header({ inverseLogo, darkMode }: HeaderProps) {

    const { headerState, headerDispatch, wrapperHeaderDispatch, wrapperKeyboardDispatch } = useHeaderState();
    const size = useWindowSize();
    const debouncedWidth = useDebounce(size.width, 300);
    
    const [ menuOpen, setMenuOpen ] = useState<boolean>(false);

    const focusRef = useRef<any>();
    const logoRef = useRef<any>();

    const toggleMenuKeyDown = (e: React.KeyboardEvent) => {
        if (e.code == 'Enter' || e.code == 'Space') {
            setMenuOpen(!menuOpen);   
            e.preventDefault(); // prevent click event
        } 
    };

    const toggleMenuClick = (e: React.MouseEvent) => {
        setMenuOpen(!menuOpen);   
    }
    
    // OPEN SOLUTIONS WHEN THE MENU OPENS IN MOBILE VIEW
    useEffect(() => {

        // OPEN SERVICES BY DEFAULT WHEN THE MENU OPENS
        if(menuOpen) {
            if( size.width && size.width < 768 && headerState.services !== 1) {
                headerDispatch({ type: 'services' });
            }    
            focusRef?.current?.focus();     
        }
        
        // ALWAYS CLOSE ALL THE SUB-MENU ITEMS WHEB THE NEMU CLOSES
        if (!menuOpen) {
            headerDispatch({ type: 'closeAll' });
            logoRef?.current?.focus();   
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedWidth, menuOpen]);    

    return ( <header className={`${ darkMode ? 'bg-dark-blue' : ''} `}>
        <div className='container mt-md-3'>
            <div className='row'>
                <div className='col'>
                    <a href='https://public.cssresearch.org'>
                        {
                            inverseLogo ?
                                <picture>
                                    <img alt='Center for the Study of Services Logo (CSS)' className='header__logo' src='assets/img/public/login/logo-white.png' />
                                </picture> :
                                <picture>
                                    <img alt='Center for the Study of Services Logo (CSS)' className='header__logo' src='assets/img/public/login/logo-dark.png' />
                                </picture>
                        }
                    </a>
                </div>
            </div>
        </div>

        <div className='navigation-menu__toggle d-flex align-items-center'>
            <a href='/login' className='login-link me-4'>
                My Account
            </a>
            <button className='ps-2' type='button' onClick={toggleMenuClick}  onKeyDown={toggleMenuKeyDown} aria-label='Open super menu' >
                <Menu className='menu-icon' /><span className='sr-only'>Menu</span>
            </button>
        </div>

        <div className={clsx('navigation-menu', {
            'active' : menuOpen,
            'inactive': !menuOpen
        })} id='navigation-menu' role='menu' ref={focusRef}>
            {
                menuOpen ? (
                    <>
                        <div className='ps-lg-6 ps-md-3'>
                            <div className='row mb-md-6'>
                                <div className='col'>
                                    <a onClick={toggleMenuClick} href='/' >
                                        <picture>
                                            <img alt='Center for the Study of Services Logo (CSS)' className='header__logo' src='assets/img/public/login/logo-white.png' />
                                        </picture>
                                    </a>
                                </div>
                            </div>
                            <div className='row ps-md-0 ps-4'>
                                <div className='col-xxl-9 col-12'>
                                    <div className='row ps-md-0'>
                                        <div className='col-md-4 mt-md-0 mt-sm-6 mt-6'>
                                            <div className='pe-md-5 pe-3'>
                                                <div className='header-navigation__title pb-md-4 pb-sm-2'>                                        
                                                    <div className='row justify-content-between open-close-menu d-md-block d-none'>
                                                        <div role='heading' aria-level={2} aria-owns='services-list' className='col-auto open-close-menu d-md-block d-none'>Services</div>
                                                    </div>
                                                    <div role='button'
                                                        onClick={() => wrapperHeaderDispatch({ type: 'services' }) }
                                                        onKeyDown={(e) => wrapperKeyboardDispatch(e, { type: 'services' })}
                                                        className='row justify-content-between open-close-menu d-md-none'>
                                                            <div aria-owns='services-list' className='col-auto open-close-menu'>Services</div>
                                                            <div className='col-auto'>
                                                                <FontAwesomeIcon icon={faPlus} className={`header-menu-status ${ headerState.services === 1 ? 'slide-down' : '' }`} />
                                                            </div>
                                                    </div>
                                                </div>
                                                <div className={`header-menu-items-container ${ headerState.services === 1 ? 'slide-down' : '' }`}>
                                                    <ul id='services-list' role={'menu'} className='header-navigation pt-md-4 pt-3'>
                                                        {
                                                            routes.services.map( (route, idx) => (
                                                                <li role='menuitem' key={`service-${idx}`}>
                                                                    {/* <Link  onClick={toggleMenuClick} href=>{route.title}</Link> */}
                                                                    <a onClick={toggleMenuClick} href={route.link}>{route.title}</a>
                                                                </li>
                                                            ))
                                                        }
                                                    </ul>                                        
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-md-4 mt-md-0 mt-sm-5 mt-5'>
                                            <div className='pe-md-5 pe-3'>
                                                <div className='header-navigation__title pb-md-4 pb-sm-2'>
                                                    <div className='row justify-content-between open-close-menu d-md-block d-none'>
                                                        <div role='heading' aria-level={2} aria-owns='services-list' className='col-auto open-close-menu d-md-block d-none'>Services</div>
                                                    </div>
                                                    <div 
                                                        role='button' 
                                                         
                                                        onClick={() => wrapperHeaderDispatch({ type: 'solutions' }) } 
                                                        onKeyDown={(e) => wrapperKeyboardDispatch(e, { type: 'solutions' })}
                                                        className='row justify-content-between open-close-menu d-md-none'>
                                                            <div aria-owns='services-list' className='col-auto open-close-menu'>Solutions</div>
                                                            <div className='col-auto'>
                                                                <FontAwesomeIcon icon={faPlus} className={`header-menu-status ${ headerState.solutions === 1 ? 'slide-down' : '' }`} />
                                                            </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className={`header-menu-items-container ${ headerState.solutions === 1 ? 'slide-down' : '' }`}>
                                                        <ul id='solutions-list' role={'menu'} className='header-navigation pt-md-4 pt-3'>
                                                            {
                                                                routes.solutions.map( (route, idx) => (
                                                                    <li key={`solutions-${idx}`} role='menuitem'>
                                                                        <a onClick={toggleMenuClick} href={route.link}>{route.title}</a>
                                                                    </li>
                                                                ))
                                                            }
                                                        </ul>
                                                    </div>                                   
                                                </div>                                     
                                            </div>
                                        </div>
                                        <div className='col-md-4 mt-md-0 mt-sm-5 mt-5'>
                                            <div className='pe-md-5 pe-3'>
                                                <div className='header-navigation__title pb-md-4 pb-sm-2'>

                                                    <div className='row justify-content-between open-close-menu d-md-block d-none'>
                                                        <div role='heading' aria-level={2} aria-owns='services-list' className='col-auto open-close-menu d-md-block d-none'>Services</div>
                                                    </div>
                                                    <div 
                                                        role='button'                                                         
                                                        onClick={() => wrapperHeaderDispatch({ type: 'company' }) } 
                                                        onKeyDown={(e) => wrapperKeyboardDispatch(e, { type: 'company' })}
                                                        className='row justify-content-between open-close-menu d-md-none'>
                                                            <div aria-owns='services-list' className='col-auto open-close-menu'>Company</div>
                                                            <div className='col-auto'>
                                                                <FontAwesomeIcon icon={faPlus} className={`header-menu-status ${ headerState.company === 1 ? 'slide-down' : '' }`} />
                                                            </div>
                                                    </div>
                                                </div> 
                                                <div>
                                                    <div className={`header-menu-items-container ${ headerState.company === 1 ? 'slide-down' : '' }`}>
                                                        <ul id='company-list' role={'menu'} className='header-navigation pt-md-4 pt-3'>
                                                            {
                                                                routes.company.map( (route, idx) => (
                                                                    <li key={`company-${idx}`} role='menuitem'>
                                                                        <a onClick={toggleMenuClick} href={route.link}>{route.title}</a>
                                                                    </li>
                                                                ))
                                                            }
                                                        </ul>
                                                    </div> 
                                                </div>                                                                       
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='col-xxl-3 col-12'>
                                    <div className='row d-md-block d-none'>
                                        <div className='col-xxl-12 col-7'>
                                            <div className='survey-question-gate row justify-content-between align-items-center'>
                                                <div className='col-12 mb-xxl-3 mb-xl-1'>
                                                    <h5>Received a survey from us?</h5>
                                                </div>
                                                <div className='col-12 mb-xxl-3'>
                                                    <div className='form-description'>
                                                        <p className='mb-xxl-4'>
                                                            {/* javascript:void(0); */}
                                                            If you have any questions about the survey, please email us at <a href='mailto:info@cssresearch.org' >info@cssresearch.org</a> or call us at <a  href=''>(202) 347-9612</a> and reference the 8-digit ID number located on the cover letter between two asterisks (*).
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type='button' onClick={toggleMenuClick}  onKeyDown={toggleMenuKeyDown}  aria-label='Close super menu' className='btn-close-menu'>
                            <X /><span className='sr-only'>Close Menu</span>
                        </button>
                    </>
                ):(<></>)
            }
        </div>
        <div className={clsx('navigation-menu__overlay',{
            'active' : menuOpen,
            'inactive': !menuOpen
        })}></div>
    </header> );
}